import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import clsx from "clsx";
import { useRequest } from "hooks/useRequest";
import { useAuth } from "contexts/AuthContext";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import api from "utils/api";
import { SmartSurveyorUploadWizard } from "./SmartSurveyorUploadWizard";
import { SmartSurveyorDataProcessingIndicator } from "./SmartySurveyorDataProcessingIndicator";
import { StatusEnum } from "common/constants";
import { canViewSmartSurveyorReview } from "../../SmartSurveyor/SmartSurveyorV3/utils/canViewSmartSurveyorReview";
import { fetchResearchReport } from "../../SmartSurveyor/SmartSurveyorV3/utils/fetchResearchReport";
import { useCreditPreflightCheck } from "../hooks/useCreditPreflightCheck";
import { useFileCategorySelection } from "../hooks/useFileCategorySelection";
import { FileCategoryCardsGrid } from "../shared/FileCategoryCardsGrid";
import { UploadedFilesModal } from "../shared/UploadedFilesModal";
import { useUnfinishedUploadFiles } from "../shared/useUnfinishedUploadFiles";

export const RudolfFritzSmartSurveyorDataUploader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  const [hasResearchReport, setHasResearchReport] = useState(false);
  const [showUploadedFilesModal, setShowUploadedFilesModal] = useState(false);

  const {
    files: unfinishedUploadFiles,
    refetch: refetchUnfinishedUploadFiles,
  } = useUnfinishedUploadFiles({ projectId });

  const {
    selectedFiles,
    setters,
    hasRequiredFiles,
    pdfFileCount,
    handleRemoveFile,
  } = useFileCategorySelection();

  const { data: creditBalance, isLoading: isCreditsLoading } = useRequest(
    async () => {
      if (user?.isGlobalAdmin) return null;
      return api.getCreditBalance();
    },
    { manual: false, defaultParams: [] },
  );

  const hasCredits = useMemo(
    () =>
      user?.isGlobalAdmin ||
      (creditBalance !== undefined &&
        creditBalance !== null &&
        creditBalance.available > 0),
    [user, creditBalance],
  );

  const {
    data: projectStatus,
    isLoading: isProjectStatusLoading,
    run: refetchProjectStatus,
  } = useRequest(
    async () => {
      if (!projectId) return;
      const { project } = await api.fetchProject(projectId);

      if (
        canViewSmartSurveyorReview({
          status: project?.status,
          isSuperAdmin: !!user?.isGlobalAdmin,
        })
      ) {
        navigate(`/project/${projectId}/smart-surveyor`);
        return;
      }

      return project?.status ?? null;
    },
    { manual: true },
  );

  useEffect(() => {
    if (projectId) {
      refetchProjectStatus();
    }
  }, [projectId, refetchProjectStatus]);

  const showsProcessingView =
    projectStatus === StatusEnum.IN_PROCESS ||
    projectStatus === StatusEnum.IN_VALIDATION;

  useEffect(() => {
    if (!projectId || !showsProcessingView) return;
    fetchResearchReport(projectId).then((report) => {
      setHasResearchReport(!!report);
    });
  }, [projectId, showsProcessingView]);

  const handleViewResearchReport = useCallback(() => {
    if (projectId) {
      navigate(`/project/${projectId}/smart-surveyor/research`);
    }
  }, [projectId, navigate]);

  const {
    isChecking: isCheckingCredits,
    error: creditError,
    runCheck: runCreditPreflightCheck,
  } = useCreditPreflightCheck({
    pdfFileCount,
    isGlobalAdmin: user?.isGlobalAdmin,
    uploadKind: "initial",
  });

  const handleAnalyzeClick = useCallback(async () => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_FILES_SUBMITTED, {
      uploadKind: "initial",
      floorPlanCount: selectedFiles.floorPlans.length,
      diagramCount: selectedFiles.electricalDiagrams.length,
      boqCount: selectedFiles.billOfQuantities.length,
      otherCount: selectedFiles.otherFiles.length,
    });

    const canProceed = await runCreditPreflightCheck();
    if (canProceed) {
      setShowWizard(true);
    }
  }, [selectedFiles, runCreditPreflightCheck]);

  if (!projectId) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <p className="text-lg text-gray-700">
          {t("smartSurveyor.noProjectSelected")}
        </p>
      </div>
    );
  }

  if (isProjectStatusLoading) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        Loading project data…
      </div>
    );
  }

  if (showsProcessingView) {
    return (
      <>
        <SmartSurveyorDataProcessingIndicator
          onViewResearchReport={
            hasResearchReport ? handleViewResearchReport : undefined
          }
          onViewUploadedFiles={
            unfinishedUploadFiles.length > 0
              ? () => setShowUploadedFilesModal(true)
              : undefined
          }
          uploadedFilesCount={unfinishedUploadFiles.length}
        />
        <UploadedFilesModal
          isOpen={showUploadedFilesModal}
          onClose={() => setShowUploadedFilesModal(false)}
          files={unfinishedUploadFiles}
        />
      </>
    );
  }

  return (
    <div className="w-full h-full flex justify-center items-start">
      <div
        className={clsx(
          "block w-full max-w-6xl px-4 md:px-8 pb-2 md:pb-4 pt-4 md:pt-12 flex flex-col items-center justify-center",
        )}
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-3 text-center">
          {t("smartSurveyor.uploadProjectFiles")}
        </h1>
        {hasCredits && creditBalance && (
          <p className="text-sm text-gray-500 mb-4 text-center">
            <Trans
              i18nKey="smartSurveyor.uploadProjectFilesSubtitle"
              values={{ available: creditBalance.available }}
              components={{
                highlight: <span className="font-semibold text-blue-600" />,
              }}
            />
          </p>
        )}
        {!isCreditsLoading && (!hasCredits || creditError) && (
          <div className="w-full mb-3 p-4 bg-red-50 border border-red-200 rounded-lg text-center">
            <p className="text-sm text-red-700 font-medium">
              {creditError || t("smartSurveyor.credits.noCreditsWarning")}
            </p>
          </div>
        )}
        <FileCategoryCardsGrid
          selectedFiles={selectedFiles}
          setters={setters}
        />
        <div className="mt-8 w-full flex flex-col items-center">
          <button
            type="button"
            onClick={handleAnalyzeClick}
            disabled={!hasCredits || !hasRequiredFiles || isCheckingCredits}
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
          >
            {isCheckingCredits
              ? t("smartSurveyor.credits.checking", "Checking credits...")
              : t("smartSurveyor.analysisWorkflow.analyzeButton")}
          </button>
        </div>
        {showWizard && (
          <SmartSurveyorUploadWizard
            projectId={Number(projectId)}
            uploadKind="initial"
            selectedFiles={selectedFiles}
            onClose={() => setShowWizard(false)}
            onUploadComplete={() => {
              refetchProjectStatus();
              refetchUnfinishedUploadFiles();
            }}
            onRemoveFile={handleRemoveFile}
          />
        )}
      </div>
    </div>
  );
};
